/*
 * Schema Utility Functions
 * 
 * Features:
 * - Helper functions for schema filtering and team membership checks
 * - Handles contact team inheritance from account
 * - Schema type checking utilities
 * 
 * Implementation:
 * - getEntityTeamId: Accounts have direct teamId, contacts inherit from account
 * - isUserTeamMember: Simple team membership check
 * - Schema type helpers for cleaner schema type checking
 * 
 * Changes:
 * - 2026-01-28: Created for schema visibility feature
 * - 2026-01-30: Added schema type helper functions
 * - 2026-01-31: Removed base schema concept
 */

import { UserTeam } from './permissions';
import { Schema } from '../context/SchemaContext';

/**
 * Get the team ID for an entity (account or contact)
 * Contacts inherit teamId from their account relationship
 * 
 * @param entity - Account or Contact object
 * @returns teamId number or null
 */
export function getEntityTeamId(entity: { teamId?: number | null; accountId?: number }): number | null {
  // Accounts have direct teamId
  if (entity.teamId !== undefined) {
    return entity.teamId;
  }

  // Contacts inherit teamId from account (handled at query level in database)
  // The entity object should already have teamId populated from the account join
  return null;
}

/**
 * Check if user is a member of a specific team
 * 
 * @param teamId - The team ID to check
 * @param userTeams - Array of teams the user belongs to
 * @returns true if user is member of the team
 */
export function isUserTeamMember(teamId: number | null | undefined, userTeams: UserTeam[]): boolean {
  if (teamId === null || teamId === undefined) {
    return false;
  }

  return userTeams.some(team => team.teamId === teamId);
}

/**
 * Check if a schema is a team schema (matches teamId and not RLP)
 * 
 * @param schema - Schema object to check
 * @param teamId - Team ID to match against
 * @returns true if schema is a team schema for the given team
 */
export function isTeamSchema(schema: Schema, teamId: number): boolean {
  return schema.teamId === teamId && !schema.rlp;
}

/**
 * Check if a schema is an RLP schema
 * 
 * @param schema - Schema object to check
 * @returns true if schema is an RLP schema
 */
export function isRlpSchema(schema: Schema): boolean {
  return !!schema.rlp;
}
